import {
  type Breakpoint,
  Dialog,
  DialogActions,
  DialogContent,
  type DialogContentProps,
  type DialogProps,
  DialogTitle,
  Skeleton,
} from "@mui/material";
import { CancelButton } from "core/components/CancelButton";
import { ErrorComponent } from "core/components/ErrorComponent";
import { ResetButton } from "core/components/ResetButton";
import { SubmitButton } from "core/components/SubmitButton";
import type { APIError } from "core/entities";
import type { ReactNode, SubmitEventHandler } from "react";

const DisabledButtons = (
  <>
    <CancelButton disabled />
    <ResetButton disabled />
    <SubmitButton disabled />
  </>
);

type DialogFormProps = {
  title: string;
  children: ReactNode;
  isLoading?: boolean;
  isError?: APIError;
  isSubmitting?: boolean;
  open: boolean;
  onDialogClose: DialogProps["onClose"];
  onSubmit: SubmitEventHandler<HTMLFormElement>;
  onCancelButtonClick: () => void;
  onResetButtonClick?: () => void;
  loadingSlot?: ReactNode;
  errorSlot?: ReactNode;
  maxWidth?: Breakpoint | false;
  scroll?: DialogProps["scroll"];
  isSubmitDisabled?: boolean;
  submitButtonLabel?: ReactNode;
  submitButtonIcon?: ReactNode;
  actionSlot?: ReactNode;
  dialogContentProps?: DialogContentProps;
  disableActions?: boolean;
  hideActions?: boolean;
  keepMounted?: boolean;
  backgroundColor?: string;
};

export function DialogFormContainer({
  title,
  children,
  open,
  onDialogClose,
  onSubmit,
  onCancelButtonClick,
  onResetButtonClick,
  isLoading,
  isError,
  isSubmitting,
  loadingSlot,
  errorSlot,
  scroll,
  maxWidth = "md",
  isSubmitDisabled = false,
  submitButtonLabel,
  submitButtonIcon,
  actionSlot,
  dialogContentProps,
  disableActions = false,
  hideActions = false,
  keepMounted = false,
  backgroundColor = "background.default",
}: DialogFormProps) {
  const Title = (
    <DialogTitle variant="h5" textAlign="center">
      {title}
    </DialogTitle>
  );
  if (isLoading) {
    return (
      <Dialog
        keepMounted={keepMounted}
        open={open}
        onClose={onDialogClose}
        maxWidth={maxWidth}
        fullWidth
        scroll={scroll}
        slotProps={{ paper: { sx: { backgroundColor: backgroundColor } } }}
      >
        {Title}
        <DialogContent>
          {loadingSlot ?? <Skeleton variant="rounded" height={200} />}
        </DialogContent>
        <DialogActions>{DisabledButtons}</DialogActions>
      </Dialog>
    );
  }
  if (isError) {
    return (
      <Dialog
        keepMounted={keepMounted}
        open={open}
        onClose={onDialogClose}
        maxWidth={maxWidth}
        fullWidth
        scroll={scroll}
        slotProps={{ paper: { sx: { backgroundColor: backgroundColor } } }}
      >
        {Title}
        <DialogContent>
          {errorSlot ?? <ErrorComponent apiError={isError} />}
        </DialogContent>
        <DialogActions>{DisabledButtons}</DialogActions>
      </Dialog>
    );
  }
  return (
    <Dialog
      open={open}
      onClose={onDialogClose}
      maxWidth={maxWidth}
      fullWidth
      scroll={scroll}
      keepMounted={keepMounted}
      slotProps={{
        paper: {
          sx: { backgroundColor: backgroundColor },
          component: "form",
          onSubmit,
        },
      }}
    >
      {Title}
      <DialogContent {...dialogContentProps}>{children}</DialogContent>
      {!hideActions && (
        <DialogActions sx={{ flexWrap: "wrap" }}>
          {disableActions ? (
            DisabledButtons
          ) : (
            <>
              {actionSlot}
              <CancelButton
                onClick={onCancelButtonClick}
                disabled={isSubmitting}
              />
              {onResetButtonClick && (
                <ResetButton
                  onClick={onResetButtonClick}
                  disabled={isSubmitting}
                />
              )}
              <SubmitButton
                loading={isSubmitting}
                disabled={isSubmitDisabled}
                startIcon={submitButtonIcon}
              >
                {submitButtonLabel}
              </SubmitButton>
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
